import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Home, MapPin, DollarSign, Clock, Eye, MessageSquare,
    Users, Target, ArrowRight, CheckCircle, Bed, Bath, Maximize,
    Star, LayoutList, Columns3
} from 'lucide-react';
import { ActionItems, ActivityFeed } from '@/components/ActivityFeed';

export default function OperatorDashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(null);
    const [smartQueue, setSmartQueue] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await API.get('/operator/active-listings');
            setListings(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    const loadAssignments = useCallback(async () => {
        try {
            const { data } = await API.get('/marketplace/assignments/');
            setAssignments(data);
        } catch (err) { console.error(err); }
    }, []);

    useEffect(() => { load(); loadAssignments(); }, [load, loadAssignments]);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="operator-dashboard-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="mb-8">
                    <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']" data-testid="operator-dashboard-title">
                        Operator Dashboard
                    </h1>
                    <p className="text-sm text-zinc-500 mt-1">
                        Active Day 91+ listings ready for buyer matching. Your role: bring buyers, close deals.
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <Home className="w-5 h-5 text-emerald-600 mb-2" />
                        <div className="text-2xl font-semibold text-zinc-900 font-['Outfit']">{listings.length}</div>
                        <div className="text-xs text-zinc-500">Active Listings</div>
                    </div>
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <Eye className="w-5 h-5 text-blue-600 mb-2" />
                        <div className="text-2xl font-semibold text-zinc-900 font-['Outfit']">
                            {listings.reduce((a, l) => a + (l.views || 0), 0)}
                        </div>
                        <div className="text-xs text-zinc-500">Total Views</div>
                    </div>
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <MessageSquare className="w-5 h-5 text-amber-600 mb-2" />
                        <div className="text-2xl font-semibold text-zinc-900 font-['Outfit']">
                            {listings.reduce((a, l) => a + (l.inquiries || 0), 0)}
                        </div>
                        <div className="text-xs text-zinc-500">Total Inquiries</div>
                    </div>
                </div>

                {/* Smart Action Items */}
                <ActionItems />

                {/* Mission Reminder */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <Target className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                        <div className="text-sm font-medium text-emerald-800">Your Mission</div>
                        <div className="text-xs text-emerald-700 mt-0.5">
                            Bring qualified buyers to these active listings. Use the Smart Queue for best-fit buyer recommendations.
                        </div>
                    </div>
                </div>

                <Tabs defaultValue="listings" className="space-y-6">
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1">
                        <TabsTrigger value="listings" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="operator-tab-listings">
                            <LayoutList className="w-4 h-4 mr-1.5" /> Listings
                        </TabsTrigger>
                        <TabsTrigger value="kanban" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="operator-tab-kanban">
                            <Columns3 className="w-4 h-4 mr-1.5" /> Deal Board
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="listings">

                {loading ? (
                    <div className="text-center py-12"><div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : listings.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                        <Home className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Active Listings Yet</h3>
                        <p className="text-sm text-zinc-500">Properties will appear here once they reach Day 91 on market.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {listings.map(l => (
                            <div key={l.id} className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden hover:shadow-md hover:border-emerald-300 transition-all"
                                data-testid={`operator-listing-${l.id}`}>
                                <div className="h-36 bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center relative">
                                    <Home className="w-10 h-10 text-zinc-300" />
                                    <Badge className="absolute top-3 left-3 bg-emerald-700 text-white text-[10px]">Day 91+ Active</Badge>
                                    {l.financing_available && (
                                        <Badge className="absolute top-3 right-3 bg-amber-600 text-white text-[10px]">Owner Finance</Badge>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="text-lg font-semibold text-emerald-700 font-['Outfit'] mb-1">
                                        ${(l.asking_price || 0).toLocaleString()}
                                    </div>
                                    <div className="text-sm text-zinc-900 mb-1">{l.property_address}</div>
                                    {(l.city || l.state) && (
                                        <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />{[l.city, l.state].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
                                        {l.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{l.bedrooms} bd</span>}
                                        {l.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{l.bathrooms} ba</span>}
                                        {l.sqft > 0 && <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" />{l.sqft.toLocaleString()} sqft</span>}
                                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{l.current_dom || l.original_dom || '91+'} DOM</span>
                                    </div>

                                    {/* Agent Contact */}
                                    {l.agent_name && (
                                        <div className="bg-zinc-50 rounded-lg p-2.5 mb-3 text-xs">
                                            <div className="text-zinc-500 mb-0.5">Contact Through Agent</div>
                                            <div className="font-medium text-zinc-900">{l.agent_name}</div>
                                            {l.agent_brokerage && <div className="text-zinc-500">{l.agent_brokerage}</div>}
                                            {l.agent_phone && <div className="text-zinc-600">{l.agent_phone}</div>}
                                            {l.agent_email && <div className="text-zinc-600">{l.agent_email}</div>}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <Button size="sm" onClick={() => setAssigning(l)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs flex-1" data-testid={`operator-assign-${l.id}`}>
                                            <Users className="w-3 h-3 mr-1" /> Assign Buyer
                                        </Button>
                                        <Button size="sm" onClick={() => navigate('/marketplace')}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid={`operator-view-${l.id}`}>
                                            View <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setSmartQueue(l)}
                                            className="border-zinc-200 text-xs" data-testid={`operator-queue-${l.id}`}>
                                            <Star className="w-3 h-3 mr-1 text-amber-500" /> Queue
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Assignments */}
                {assignments.length > 0 && (
                    <div className="mt-8">
                        <h2 className="text-lg font-medium text-zinc-900 mb-4 font-['Outfit']" data-testid="assignments-title">Your Buyer Assignments ({assignments.length})</h2>
                        <div className="space-y-3">
                            {assignments.map(a => (
                                <div key={a.id} className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm flex items-center justify-between" data-testid={`assignment-${a.id}`}>
                                    <div>
                                        <div className="text-sm font-medium text-zinc-900">{a.property_address}</div>
                                        <div className="text-xs text-zinc-500">Buyer: {a.buyer_name} ({a.buyer_email})</div>
                                    </div>
                                    <Badge className={a.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                                        {a.status}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                    </TabsContent>

                    {/* ── KANBAN BOARD ── */}
                    <TabsContent value="kanban">
                        <KanbanBoard />
                    </TabsContent>
                </Tabs>

                {/* Activity Feed */}
                <div className="mt-6">
                    <ActivityFeed limit={8} />
                </div>
            </div>

            {assigning && <AssignBuyerModal listing={assigning} onClose={() => { setAssigning(null); load(); loadAssignments(); }} />}
            {smartQueue && <SmartQueueModal listing={smartQueue} onClose={() => setSmartQueue(null)} onAssign={(l) => { setSmartQueue(null); setAssigning(l); }} />}
        </div>
    );
}

function AssignBuyerModal({ listing, onClose }) {
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!email) return;
        setLoading(true);
        setError('');
        try {
            await API.post('/marketplace/assign-buyer', {
                listing_id: listing.id,
                buyer_email: email,
                notes,
            });
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Assignment failed');
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="assign-buyer-modal">
                <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit'] mb-2">Assign Buyer</h2>
                <p className="text-sm text-zinc-500 mb-4">
                    Match a buyer to <strong>{listing.property_address}</strong>
                </p>
                {error && <div className="mb-3 p-2 rounded bg-red-50 text-red-600 text-sm">{error}</div>}
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-zinc-600 block mb-1">Buyer Email</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                            placeholder="buyer@example.com"
                            className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm" data-testid="assign-buyer-email" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-600 block mb-1">Notes (optional)</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="e.g., Pre-qualified buyer, $25K down payment ready"
                            className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm min-h-16" />
                    </div>
                </div>
                <div className="flex gap-2 mt-4">
                    <Button onClick={submit} disabled={loading || !email}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="assign-buyer-submit">
                        {loading ? 'Assigning...' : 'Assign Buyer'}
                    </Button>
                    <Button variant="outline" onClick={onClose} className="border-zinc-200">Cancel</Button>
                </div>
            </div>
        </div>
    );
}


function SmartQueueModal({ listing, onClose, onAssign }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get(`/autonomy/smart-queue/${listing.id}`)
            .then(r => setData(r.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [listing.id]);

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="smart-queue-modal">
                <div className="p-5 border-b border-zinc-200">
                    <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit']">Smart Queue</h2>
                    <p className="text-sm text-zinc-500">Best-fit buyers for <strong>{listing.property_address}</strong> — ranked by match score</p>
                </div>
                <div className="overflow-y-auto flex-1 p-5">
                    {loading ? (
                        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                    ) : !data?.ranked_buyers?.length ? (
                        <div className="text-center py-8 text-sm text-zinc-500">No qualified buyers found for this listing.</div>
                    ) : (
                        <div className="space-y-3">
                            {data.ranked_buyers.map((b, i) => (
                                <div key={i} className={`rounded-xl border p-4 flex items-center gap-4 ${b.interested ? 'border-emerald-300 bg-emerald-50/50' : 'border-zinc-200 bg-white'}`}>
                                    <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-bold text-zinc-500 shrink-0">
                                        #{i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-zinc-900">{b.buyer_name}</span>
                                            {b.interested && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Interested</Badge>}
                                        </div>
                                        <div className="text-xs text-zinc-500">{b.buyer_email}</div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                                            <span>Credit: {b.credit_score_range}</span>
                                            <span>Down: ${(b.down_payment || 0).toLocaleString()}</span>
                                            <span>Location: {b.target_location}</span>
                                        </div>
                                    </div>
                                    <div className="text-center shrink-0">
                                        <div className={`text-xl font-bold font-['Outfit'] ${b.match_score >= 70 ? 'text-emerald-600' : b.match_score >= 50 ? 'text-amber-600' : 'text-zinc-400'}`}>
                                            {b.match_score}%
                                        </div>
                                        <div className="text-[10px] text-zinc-400">match</div>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => { onClose(); onAssign && onAssign(listing); }}
                                        className="border-blue-200 text-blue-700 text-xs shrink-0">
                                        <Users className="w-3 h-3 mr-1" /> Assign
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-zinc-200">
                    <Button variant="outline" onClick={onClose} className="border-zinc-200 w-full">Close</Button>
                </div>
            </div>
        </div>
    );
}

function KanbanBoard() {
    const [kanban, setKanban] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get('/autonomy/kanban')
            .then(r => setKanban(r.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, []);

    const columns = [
        { key: 'matched', label: 'Matched', color: 'border-t-blue-500' },
        { key: 'negotiation', label: 'Negotiation', color: 'border-t-amber-500' },
        { key: 'finalized', label: 'Finalized', color: 'border-t-emerald-500' },
        { key: 'closed', label: 'Closed', color: 'border-t-purple-500' },
    ];

    if (loading) return <div className="text-center py-12"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

    return (
        <div className="grid grid-cols-4 gap-4" data-testid="kanban-board">
            {columns.map(col => (
                <div key={col.key} className={`bg-zinc-50 rounded-xl border border-zinc-200 border-t-4 ${col.color} min-h-[300px]`}>
                    <div className="p-3 border-b border-zinc-200">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-zinc-700">{col.label}</span>
                            <span className="text-xs text-zinc-400 bg-zinc-200 rounded-full px-2 py-0.5">{kanban?.[col.key]?.length || 0}</span>
                        </div>
                    </div>
                    <div className="p-2 space-y-2">
                        {(kanban?.[col.key] || []).map(d => (
                            <div key={d.id} className="bg-white rounded-lg border border-zinc-200 p-3 shadow-sm hover:shadow transition-shadow">
                                <div className="text-xs font-medium text-zinc-900 mb-1 truncate">{d.property_address}</div>
                                <div className="text-[10px] text-zinc-500">Buyer: {d.buyer_name || 'TBD'}</div>
                                <div className="flex items-center gap-2 mt-2 text-[10px] text-zinc-400">
                                    <DollarSign className="w-3 h-3" />
                                    <span>${(d.monthly_payment || 0).toLocaleString()}/mo</span>
                                </div>
                            </div>
                        ))}
                        {(!kanban?.[col.key] || kanban[col.key].length === 0) && (
                            <div className="text-center py-6 text-xs text-zinc-400">No deals</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
